import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Visibility } from "@mui/icons-material";
import axios from "axios";
import ReportHero from "./ReportHero";
import ActionModal from "./Modal/ActionModal";
import ReceiptActionModal from "./Modal/ReceiptActionModal";
import { getAuthCredentials } from "../../utils/auth";
import generateZReadingPDF from "./Format/ZReading";
import generateXReadingPDF from "./Format/XReading";
import generateReceiptPdf from "../../utils/receiptPdf";
import generateFreeMealPdf from "../../utils/freeMealPdf";
import TitleSetter from "../../component/TitleSetter";

const ReportRecords = () => {
  // activeTab: 0 = Invoices (Complete), 1 = Pending, 2 = Free Meal, 3 = X-Reading, 4 = Z-Reading
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [records, setRecords] = useState([]);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchDate, setSearchDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [receiptConfig, setReceiptConfig] = useState(null);
  const [receiptActionModalOpen, setReceiptActionModalOpen] = useState(false);
  const [completeCount, setCompleteCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [freeMealCount, setFreeMealCount] = useState(0);
  const { token } = getAuthCredentials();

  // Fetch records based on selected date, active tab and page.
  const fetchRecords = async (date = "", tab = activeTab, page = 1) => {
    if (!date) return;

    try {
      setLoading(true);
      setRecords([]);
      setFilteredRecords([]);

      let endpoint = "";
      if (tab === 0) {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/receipts/complete`;
      } else if (tab === 1) {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/receipts/pending`;
      } else if (tab === 2) {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/receipts/free-meal`;
      } else if (tab === 3) {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/xreadings`;
      } else if (tab === 4) {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/zreadings`;
      }

      const response = await axios.get(endpoint, {
        params: { startDate: date, endDate: date, page, limit: 10 },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (tab === 0) {
        const receipts = response.data.receipts || [];
        setRecords(receipts);
        setFilteredRecords(receipts);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
        setCompleteCount(response.data.totalRecords || receipts.length);
      } else if (tab === 1) {
        const receipts = response.data.receipts || [];
        setRecords(receipts);
        setFilteredRecords(receipts);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
        setPendingCount(response.data.totalRecords || receipts.length);
      } else if (tab === 2) {
        const receipts = response.data.receipts || [];
        setRecords(receipts);
        setFilteredRecords(receipts);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
        setFreeMealCount(response.data.totalRecords || receipts.length);
      } else if (tab === 3) {
        setRecords(response.data.xReadings || []);
        setFilteredRecords(response.data.xReadings || []);
      } else if (tab === 4) {
        setRecords(response.data.zReadings || []);
        setFilteredRecords(response.data.zReadings || []);
      }
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for complete and pending receipts on initial load.
  const fetchReceiptCounts = async (date) => {
    try {
      const [completeRes, pendingRes, freeMealRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/receipts/complete`,
          {
            params: { startDate: date, page: 1, limit: 1 },
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/receipts/pending`,
          {
            params: { startDate: date, page: 1, limit: 1 },
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/receipts/free-meal`,
          {
            params: { startDate: date, page: 1, limit: 1 },
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
      ]);

      setCompleteCount(completeRes.data.totalRecords || 0);
      setPendingCount(pendingRes.data.totalRecords || 0);
      setFreeMealCount(freeMealRes.data.totalRecords || 0);
    } catch (error) {
      console.error("Error fetching receipt counts:", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setCurrentPage(1);
    fetchRecords(searchDate, newValue, 1);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    fetchRecords(searchDate, activeTab, newPage);
  };

  const handleActionModalOpen = (type, receipt) => {
    setSelectedReceipt(receipt);
    setActionModalOpen(true);
  };

  const handleActionModalClose = () => {
    setActionModalOpen(false);
    setSelectedReceipt(null);
  };

  const handleReceiptAction = async (action) => {
    const receiptData = {
      posclient: true,
      posId: receiptConfig?.posId,
      menuItems: selectedReceipt.menuItems,
      discountType: selectedReceipt.discountType,
      salesType: selectedReceipt.salesType,
      orderNumber: selectedReceipt.orderNumber,
      customerCount: selectedReceipt.customerCount,
      receiptNumber: selectedReceipt.receiptNumber,
      voucherCode: selectedReceipt.voucherCode,
      discount: selectedReceipt.discount,
      timestamp: selectedReceipt.createdAt,
      paymentType: selectedReceipt.paymentType,
      status: selectedReceipt.status,
      refReceiptNumber: selectedReceipt.refReceiptNumber,
      invoiceNumber: selectedReceipt.invoiceNumber,
      refInvoiceNumber: selectedReceipt.refInvoiceNumber,
      dateGenerated: selectedReceipt.dateGenerated,
      printCount: selectedReceipt.printCount,
      voidCount: selectedReceipt.voidCount,
    };

    if (action === "print") {
      try {
        if (!["voided", "freeMeal"].includes(selectedReceipt.status)) {
          const endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/generate-receipt?client=android`;
    
          const { data } = await axios.post(endpoint, receiptData, {
            headers: { Authorization: `Bearer ${token}` }
          });
    
          const lines = data?.data?.receiptLines || [];
          const inv   = selectedReceipt.invoiceNumber || "NA";
          const rcp   = selectedReceipt.receiptNumber || "NA";
    
          generateReceiptPdf(lines, {
            filename: `receipt-SI-${inv}-${rcp}.pdf`
          });
        }
    
        else {
          const endpoint =
            selectedReceipt.status === "voided"
              ? `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/generate-void-receipt?disposition=print`
              : `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/generate-free-meal-receipt?disposition=print`;
    
          const response = await axios.post(endpoint, receiptData, {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` }
          });
    
          const pdfBlob = new Blob([response.data], { type: "application/pdf" });
          const pdfUrl  = URL.createObjectURL(pdfBlob);
          const inv     = selectedReceipt.invoiceNumber || "NA";
          const rcp     = selectedReceipt.receiptNumber || "NA";
    
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = `receipt_data-SI-${inv}-${rcp}.pdf`;
          link.click();
        }
      } catch (error) {
        console.error("Error generating receipt:", error);
      }
    }
    
    else if (action === "view") {
      try {
        if (!["voided", "freeMeal"].includes(selectedReceipt.status)) {
          const endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/generate-receipt?client=android`;
          const { data } = await axios.post(endpoint, receiptData, {
            headers: { Authorization: `Bearer ${token}` },
          });
    
          const lines = data?.data?.receiptLines || [];
          const inv = selectedReceipt.invoiceNumber || "NA";
          const rcp = selectedReceipt.receiptNumber || "NA";
    
          const { pdfBlob } = generateReceiptPdf(lines, {
            filename: `receipt-SI-${inv}-${rcp}.pdf`,
            returnBlob: true,
          }) || {};
    
          if (pdfBlob instanceof Blob) {
            const url = URL.createObjectURL(pdfBlob);
            const w = window.open("", "_blank");
            if (w) {
              w.document.write(`
                <html>
                  <head><title>Receipt ${inv}-${rcp}</title><meta charset="utf-8"/></head>
                  <body style="margin:0">
                    <iframe id="__pdf" src="${url}" style="border:0;width:100vw;height:100vh"></iframe>
                    <script>
                      window.addEventListener("unload", function(){ try{ URL.revokeObjectURL("${url}"); }catch(e){} });
                      setTimeout(function(){ try{ URL.revokeObjectURL("${url}"); }catch(e){} }, 120000);
                    <\/script>
                  </body>
                </html>
              `);
              w.document.close();
            } else {
              window.open(url, "_blank");
              setTimeout(() => { try { URL.revokeObjectURL(url); } catch(e){} }, 120000);
            }
          }
        } else if (selectedReceipt.status === "voided") {
          const endpoint = `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/generate-void-receipt`;

          const response = await axios.post(endpoint, receiptData, {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` },
          });
    
          const pdfBlob = new Blob([response.data], { type: "application/pdf" });
          const url = URL.createObjectURL(pdfBlob);
          const inv = selectedReceipt.invoiceNumber || "NA";
          const rcp = selectedReceipt.receiptNumber || "NA";
    
          const w = window.open("", "_blank");
          if (w) {
            w.document.write(`
              <html>
                <head><title>Receipt ${inv}-${rcp}</title><meta charset="utf-8"/></head>
                <body style="margin:0">
                  <iframe id="__pdf" src="${url}" style="border:0;width:100vw;height:100vh"></iframe>
                  <script>
                    window.addEventListener("unload", function(){ try{ URL.revokeObjectURL("${url}"); }catch(e){} });
                    setTimeout(function(){ try{ URL.revokeObjectURL("${url}"); }catch(e){} }, 120000);
                  <\/script>
                </body>
              </html>
            `);
            w.document.close();
          } else {
            window.open(url, "_blank");
            setTimeout(() => { try { URL.revokeObjectURL(url); } catch(e){} }, 120000);
          }
        } else {
          const inv = selectedReceipt.invoiceNumber || "NA";
          const rcp = selectedReceipt.receiptNumber || "NA";
        
          const receiptForPdf = {
            orderNumber:  selectedReceipt.orderNumber,
            createdAt:    selectedReceipt.createdAt,
            machineType:  selectedReceipt.machineType,
            paymentType:  selectedReceipt.paymentType,
            employeeName: selectedReceipt.employeeName,
            cashierName:  selectedReceipt.cashierName,
            items:        selectedReceipt.items || selectedReceipt.menuItems || [],
          };
        
            const { pdfBlob } = generateFreeMealPdf(
              { config: receiptConfig, receipt: receiptForPdf },
              { filename: `receipt_free-meal-SI-${inv}-${rcp}.pdf` }
            ) || {};
        
            if (pdfBlob instanceof Blob) {
              const url = URL.createObjectURL(pdfBlob);
              const a = document.createElement("a");
              a.href = url;
              a.target = "_blank";
              a.rel = "noopener";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              setTimeout(() => { try { URL.revokeObjectURL(url); } catch(e){} }, 120000);
            }
        }
      } catch (error) {
        console.error("Error generating receipt:", error);
      }
    }
    
    else if (action === "headerless") {
      try {
        const endpoint =
          selectedReceipt.status === "voided"
            ? `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/generate-void-receipt`
            : `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/generate-headerless-receipt`;
    
        const response = await axios.post(endpoint, receiptData, {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` }
        });
    
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl  = URL.createObjectURL(pdfBlob);
        const inv     = selectedReceipt.invoiceNumber || "NA";
        const rcp     = selectedReceipt.receiptNumber || "NA";
    
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `receipt_headerless-SI-${inv}-${rcp}.pdf`;
        link.click();
      } catch (error) {
        console.error("Error generating receipt:", error);
      }
    }
    setReceiptActionModalOpen(false);
  };

  const handleDateSearch = (e) => {
    const value = e.target.value;
    setSearchDate(value);
    setRecords([]);
    setFilteredRecords([]);
    fetchRecords(value, activeTab, 1);
    fetchReceiptCounts(value);
  };

  const handleViewClick = (record) => {
    if (!receiptConfig) {
      console.error(
        "Receipt configuration not found. Please wait, loading configuration."
      );
      return;
    }
    if (!record) {
      console.error("Record data not found.");
      return;
    }
    try {
      let pdfUrl = "";
      if (activeTab === 3) {
        pdfUrl = generateXReadingPDF(receiptConfig, record);
      } else if (activeTab === 4) {
        pdfUrl = generateZReadingPDF(receiptConfig, record);
      }
      if (pdfUrl) {
        window.open(pdfUrl, "_blank");
      } else {
        console.error("PDF URL is empty.");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleVarianceClick = async (reading) => {
    const type = activeTab === 3 ? "X" : "Z";
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/reports/tender-variance`,
        { readingId: reading._id, readingType: type },
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const d   =
        type === "X"
          ? new Date(reading.startDate).toISOString().slice(0, 10)
          : new Date(reading.date).toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TenderVariance_${type}_${d}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading variance report:", err);
    }
  };

  useEffect(() => {
    if (!searchDate) {
      const currentDate = new Date().toISOString().split("T")[0];
      setSearchDate(currentDate);
      fetchRecords(currentDate, activeTab, 1);
      fetchReceiptCounts(currentDate);
    }
  }, []);

  useEffect(() => {
    fetchRecords(searchDate, activeTab, currentPage);
  }, [activeTab, currentPage]);

  useEffect(() => {
    const fetchReceiptConfig = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/config`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setReceiptConfig(response.data.receiptConfig || null);
      } catch (error) {
        console.error("Error fetching receipt configuration:", error);
        setReceiptConfig(null);
      }
    };

    fetchReceiptConfig();
  }, [token]);

  return (
    <Box sx={{ flexGrow: 1, padding: 3 }}>
      <TitleSetter title="Invoices & Readings" />
      <ReportHero />
      <Typography variant="h5" textAlign="center" gutterBottom>
        {activeTab === 0
          ? "Records"
          : activeTab === 1
          ? "Records"
          : activeTab === 2
          ? "Records"
          : activeTab === 3
          ? "Records"
          : "Records"}
      </Typography>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        centered
        sx={{ marginBottom: 3 }}
      >
        <Tab label={`Invoices (${completeCount})`} />
        <Tab label={`Pending (${pendingCount})`} />
        <Tab label={`Free Meal (${freeMealCount})`} />
        <Tab label="X-Reading" />
        <Tab label="Z-Reading" />
      </Tabs>
      <Box
        sx={{
          marginBottom: 3,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <TextField
          label="Search by Date"
          type="date"
          value={searchDate}
          onChange={handleDateSearch}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ maxWidth: 400 }}
        />
      </Box>
      <Paper elevation={3} sx={{ padding: 2 }}>
        {loading ? (
          <Typography variant="body1" textAlign="center">
            Loading records...
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                  <>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Order # | Tent #</TableCell>

                    {(activeTab === 0 || activeTab === 1) && (
                      <TableCell>Invoice No.</TableCell>
                    )}
                  </>
                )}
                {(activeTab === 2) && (
                  <TableCell>Employee Name</TableCell>
                )}
                <TableCell>
                  {activeTab === 0 || activeTab === 1 || activeTab === 2 ? "Order" : "Date"}
                </TableCell>
                {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                  <TableCell>Trans. Type</TableCell>
                )}
                {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                  <TableCell>Payment Type</TableCell>
                )}
                {activeTab === 3 && <TableCell>Shift</TableCell>}
                {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                  <TableCell>Total Sales</TableCell>
                )}
                {(activeTab === 3 || activeTab === 4) && (
                  <TableCell>Total Gross Sales</TableCell>
                )}
                {(activeTab === 3 || activeTab === 4) && (
                  <TableCell>Total Net / Tenders</TableCell>
                )}
                {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                  <TableCell>Status | Remarks</TableCell>
                )}
                {(activeTab === 0 ) && (
                  <TableCell>Assisted</TableCell>
                )}
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((record) => (
                <TableRow
                  key={record.receiptId || record._id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                    <>
                      <TableCell style={{ display: "none" }}>
                        {record._id || "N/A"}
                      </TableCell>
                      <TableCell>
                        {new Date(record.createdAt).toLocaleString("en-PH", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Click to copy Receipt Number">
                          <span
                            onClick={() => {
                              navigator.clipboard.writeText(
                                record.receiptNumber || ""
                              );
                            }}
                            style={{
                              cursor: "pointer",
                              color: "#1976d2",
                              fontWeight: "bold",
                            }}
                          >
                            #{record.orderNumber || "N/A"} |{" "}
                            {record.tentNumber || "N/A"}
                          </span>
                        </Tooltip>
                      </TableCell>
                      {(activeTab === 0 || activeTab === 1) && (
                        <TableCell>{record.invoiceNumber || "N/A"}</TableCell>
                      )}
                    </>
                  )}
                  {(activeTab === 2) && (
                    <TableCell>{record.employeeName || record.cashierName || "N/A"}</TableCell>
                  )}
                  <TableCell>
                    {activeTab === 0 || activeTab === 1 || activeTab === 2
                      ? record.items.map((item) => item.name).join(", ")
                      : new Date(
                          record.startDate || record.date
                        ).toLocaleDateString()}
                  </TableCell>
                  {activeTab === 3 && (
                    <TableCell>
                      {record.startDate && record.endDate
                        ? `${new Date(record.startDate).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })} - ${new Date(record.endDate).toLocaleTimeString(
                            [],
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}`
                        : "N/A"}
                    </TableCell>
                  )}
                  {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                    <TableCell>
                      {record.salesType === "DineIn"
                        ? "Dine-In"
                        : record.salesType === "TakeOut"
                        ? "Take-Out"
                        : record.salesType === "Delivery"
                        ? "Delivery"
                        : record.salesType || "N/A"}
                    </TableCell>
                  )}
                  {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                    <TableCell>
                      {record.paymentType?.toLowerCase().startsWith("e-cash") &&
                      record.status === "success" &&
                      record.subTenderDetails ? (
                        <Tooltip title={record.subTenderDetails}>
                          <span
                            onClick={() =>
                              navigator.clipboard.writeText(
                                record.subTenderDetails
                              )
                            }
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            {record.paymentType}
                          </span>
                        </Tooltip>
                      ) : (
                        record.paymentType || "N/A"
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {(
                      record.grossSales ??
                      record.totalSales ??
                      record.total
                    )?.toLocaleString("en-PH", {
                      style: "currency",
                      currency: "PHP",
                    }) || "N/A"}
                  </TableCell>
                  {(activeTab === 3 || activeTab === 4) && (
                    <TableCell>
                      {(() => {
                        const net = Number(record.netTotal || 0);
                        const totalTender = Number(
                          record?.tenderSummary?.find(
                            (t) => t.tenderType === "TOTAL"
                          )?.tenderAmount || 0
                        );
                        const mismatch = Math.abs(net - totalTender) > 0.009;
                        const difference = net - totalTender;

                        return (
                          <>
                            {net.toLocaleString("en-PH", {
                              style: "currency",
                              currency: "PHP",
                            })}{" "}
                            |{" "}
                            {totalTender.toLocaleString("en-PH", {
                              style: "currency",
                              currency: "PHP",
                            })}
                            {mismatch && (
                              <Tooltip
                                title={
                                  difference > 0
                                    ? "If Net Sales is higher than Tender Summary (SHORT) - possible cause: Manual Change was issued"
                                    : "If Net Sales is lower than Tender Summary (OVER) - possible cause: Check Kiosk Cash and Assisted Order and validate if it is tally with Cash On Hand"
                                }
                              >
                                <ReportProblemOutlinedIcon
                                  fontSize="small"
                                  sx={{
                                    color: "#f57c00",
                                    marginLeft: 1,
                                    verticalAlign: "middle",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                  )}
                  {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
                    <TableCell>
                      {record.status || "N/A"}
                      {record.remarks ? ` | ${record.remarks}` : ""}
                    </TableCell>
                  )}
                  {(activeTab === 0) && (
                    <TableCell>
                      {record.cashierName || ""}
                    </TableCell>
                  )}
                  <TableCell>
                    <Tooltip
                      title={
                        activeTab !== 0 && !receiptConfig
                          ? "Loading configuration, please wait"
                          : "View"
                      }
                    >
                      <span>
                        <IconButton
                          disabled={activeTab !== 0 && !receiptConfig}
                          onClick={() =>
                            activeTab === 0 || activeTab === 1 || activeTab === 2
                              ? (setSelectedReceipt(record),
                                setReceiptActionModalOpen(true))
                              : handleViewClick(record)
                          }
                        >
                          <Visibility />
                        </IconButton>
                        {(activeTab === 3 || activeTab === 4) &&
                        (() => {
                          const readingDate =
                            activeTab === 3 ? record.startDate : record.date;
                          return new Date(readingDate) >= new Date("2025-06-12");
                        })() && (
                          <Tooltip title="Variance Report">
                            <IconButton sx={{ ml: 0.5 }} onClick={() => handleVarianceClick(record)}>
                              <DescriptionOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {filteredRecords.length === 0 && !loading && (
          <Typography
            variant="body1"
            textAlign="center"
            color="textSecondary"
            sx={{ marginTop: 2 }}
          >
            No records found.
          </Typography>
        )}
      </Paper>
      {(activeTab === 0 || activeTab === 1 || activeTab === 2) && filteredRecords.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
          />
        </Box>
      )}
      <ActionModal
        fetchRecords={fetchRecords}
        open={actionModalOpen}
        handleClose={handleActionModalClose}
        receipt={selectedReceipt}
        onActionComplete={() => fetchRecords(searchDate, activeTab)}
      />
      {receiptConfig && selectedReceipt && (
        <ReceiptActionModal
          open={receiptActionModalOpen}
          handleClose={() => setReceiptActionModalOpen(false)}
          handleAction={handleReceiptAction}
          posId={receiptConfig.posId}
          receipt={selectedReceipt}
        />
      )}
    </Box>
  );
};

export default ReportRecords;
